import {
  absoluteUrl,
  articleLd,
  breadcrumbLd,
  eventLd,
  itemListLd,
  localBusinessLd,
  organizationLd,
  productLd,
  touristTripLd,
  webSiteLd,
} from './jsonLd'
import type { JsonLd, RouteSeo, SeoCrumb, SeoMeta, SeoSite } from './types'

/**
 * One page's head, as data.
 *
 * `seoTags` is pure: no DOM, no React, no `import.meta`. It has to be, because it is
 * rendered **twice** by two different things —
 *
 *   `applySeo`            writes the list into `document.head` as a visitor navigates
 *   `tools/vite-seo.ts`   writes the same list into `dist/<route>/index.html` at build time
 *
 * — and those two must never disagree. A crawler that does not run JavaScript reads the
 * second; a visitor and Googlebot's rendering pass read the first. When they drift, the
 * page a search engine indexes stops being the page a person sees, and nothing in a
 * browser shows it. One list, two renderers, is the whole design.
 */

/** A single head element, in the order it should be written. */
export type SeoTag =
  | { kind: 'title'; text: string }
  | { kind: 'meta'; name?: string; property?: string; content: string }
  | { kind: 'link'; rel: string; href: string }
  | { kind: 'jsonld'; json: string }

/** Robots directives for a page we want indexed. */
const INDEXABLE = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

/**
 * `noindex` and `nofollow` together.
 *
 * `nofollow` is included on purpose. Every page marked `noindex` here is placeholder
 * content — a fabricated business, a template detail page, the 404 — and the links on it
 * point at more of the same.
 */
const HIDDEN = 'noindex, nofollow'

/** `/x/`, `x`, `//x` → `/x`. The root stays `/`. */
export const normalizePath = (path: string): string => {
  const withSlash = path.startsWith('/') ? path : `/${path}`
  const trimmed = withSlash.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed.replace(/\/{2,}/g, '/')
}

/** The trail for a page: Home, its ancestors, then itself. */
const trailFor = (meta: SeoMeta): SeoCrumb[] => [
  { name: 'Home', path: '/' },
  ...(meta.trail ?? []),
  { name: meta.name ?? meta.title, path: normalizePath(meta.path) },
]

/**
 * The schema.org nodes for one page.
 *
 * Site-level nodes land on the home route and nowhere else: repeating an `Organization`
 * on all eighteen pages of a site tells a search engine nothing it did not learn the first
 * time, and it is eighteen places for one fact to go stale.
 */
const nodesFor = (site: SeoSite, meta: SeoMeta): JsonLd[] => {
  const path = normalizePath(meta.path)
  const nodes: JsonLd[] = []

  if (path === '/' && site.schema) {
    const { organization, website, business } = site.schema
    if (organization) nodes.push(organizationLd(site, organization))
    if (website) nodes.push(webSiteLd(site, website))
    if (business) nodes.push(localBusinessLd(site, business))
  }

  // Every page below the root gets a trail. The home page's would be one item long,
  // which Google ignores and which reads as noise in the source.
  if (path !== '/') nodes.push(breadcrumbLd(site, trailFor(meta)))

  if (meta.itemList) nodes.push(itemListLd(site, meta.itemList))
  if (meta.article) nodes.push(articleLd(site, path, meta.article))
  if (meta.event) nodes.push(eventLd(site, path, meta.event))
  if (meta.product) nodes.push(productLd(site, path, meta.product))
  if (meta.trip) nodes.push(touristTripLd(site, path, meta.trip))

  return nodes
}

/**
 * Serialises one node.
 *
 * `<` is escaped to `\u003c` because a `</script>` inside a JSON string closes the block
 * that carries it — the classic way a JSON-LD payload turns into a broken page. The
 * escape is valid JSON and parses back to the same string.
 */
const serialize = (node: JsonLd): string =>
  JSON.stringify({ '@context': 'https://schema.org', ...node }).replace(/</g, '\\u003c')

/**
 * The complete head for one page.
 *
 * @param site the app's production identity — see `SeoSite`, and note that `origin` is
 *   always the production host so a preview deployment still names the real page.
 * @param meta this page.
 * @param indexable `false` for a build that must not be indexed at all — a Cloudflare
 *   preview branch. It overrides the page's own `noindex`, never the other way round.
 */
export function seoTags(site: SeoSite, meta: SeoMeta, indexable = true): SeoTag[] {
  const url = absoluteUrl(site.origin, normalizePath(meta.path))
  const image = meta.image ?? site.defaultImage
  const imageUrl = absoluteUrl(site.origin, image.url)
  const type = meta.type ?? 'website'

  const tags: SeoTag[] = [
    { kind: 'title', text: meta.title },
    { kind: 'meta', name: 'description', content: meta.description },
    { kind: 'meta', name: 'robots', content: indexable && !meta.noindex ? INDEXABLE : HIDDEN },
    { kind: 'link', rel: 'canonical', href: url },

    { kind: 'meta', property: 'og:type', content: type },
    { kind: 'meta', property: 'og:site_name', content: site.name },
    { kind: 'meta', property: 'og:locale', content: site.locale },
    { kind: 'meta', property: 'og:title', content: meta.title },
    { kind: 'meta', property: 'og:description', content: meta.description },
    { kind: 'meta', property: 'og:url', content: url },
    { kind: 'meta', property: 'og:image', content: imageUrl },
    { kind: 'meta', property: 'og:image:alt', content: image.alt },
  ]

  if (image.width !== undefined) {
    tags.push({ kind: 'meta', property: 'og:image:width', content: String(image.width) })
  }
  if (image.height !== undefined) {
    tags.push({ kind: 'meta', property: 'og:image:height', content: String(image.height) })
  }
  if (type === 'article') {
    if (meta.publishedTime) {
      tags.push({ kind: 'meta', property: 'article:published_time', content: meta.publishedTime })
    }
    if (meta.modifiedTime) {
      tags.push({ kind: 'meta', property: 'article:modified_time', content: meta.modifiedTime })
    }
  }

  // `summary_large_image` on every page: the four sites are photographic, and the small
  // card crops a landscape photograph into an unreadable square.
  tags.push(
    { kind: 'meta', name: 'twitter:card', content: 'summary_large_image' },
    { kind: 'meta', name: 'twitter:title', content: meta.title },
    { kind: 'meta', name: 'twitter:description', content: meta.description },
    { kind: 'meta', name: 'twitter:image', content: imageUrl },
    { kind: 'meta', name: 'twitter:image:alt', content: image.alt },
  )

  for (const node of nodesFor(site, meta)) {
    tags.push({ kind: 'jsonld', json: serialize(node) })
  }

  return tags
}

/**
 * The site tree, indexed by path, with each entry's breadcrumb trail filled in.
 *
 * A trail is derived rather than authored: `/tours/off-road-day-tour` walks up to
 * `/tours`, finds its `name`, and stops at the root. Authoring trails by hand is how a
 * page ends up with a breadcrumb that disagrees with the one drawn on it.
 */
export function routeSeoIndex(routes: RouteSeo[]): Record<string, RouteSeo> {
  const byPath: Record<string, RouteSeo> = {}
  for (const route of routes) byPath[normalizePath(route.path)] = route

  for (const [path, route] of Object.entries(byPath)) {
    if (route.trail !== undefined || path === '/') continue
    const trail: SeoCrumb[] = []
    const segments = path.split('/').filter(Boolean)
    for (let depth = 1; depth < segments.length; depth++) {
      const ancestorPath = `/${segments.slice(0, depth).join('/')}`
      const ancestor = byPath[ancestorPath]
      if (ancestor) trail.push({ name: ancestor.name ?? ancestor.title, path: ancestorPath })
    }
    byPath[path] = { ...route, trail }
  }

  return byPath
}

/**
 * The routes that belong in a sitemap: everything not marked `noindex`.
 *
 * A sitemap is a list of pages we are asking to have indexed, so a `noindex` page in one
 * is a contradiction — and Search Console reports it as an error against the whole file,
 * not against the one URL.
 */
export const sitemapRoutes = (routes: RouteSeo[]): RouteSeo[] => routes.filter((route) => !route.noindex)
